/**
 * Utility for detecting existing configuration in an organization.
 * Used by the onboarding flow to determine which steps have already been completed
 * based on what's actually configured, enabling smart step skipping for
 * returning or partially-onboarded users.
 */

import { getAgencyConnections } from "@/actions-client/settings/actions";
import { getConnectedIntegrations } from "@/actions/integrations/actions";
import { getMessagingVerificationStatus } from "@/actions/settings/sms/actions";
import { isNodeProviderConnected } from "@/actions/workflows/actions";
import { ProductFocus } from "@/constants/onboarding";
import { MessagingVerificationStatus } from "@/enums/messaging";
import { CALENDAR_APPLICATION } from "@/enums/profile";
import { Org } from "@/types/org";
import type { AgentProfile } from "@/types/profile";
import { apiLive } from "@/lib/api";
import { unwrapSessionExpiry } from "@/utils/common/sessionExpiry";
import { GAIL_API } from "@/utils/common/constants/routes";
import { isScriptsApiV2 } from "@/utils/org/scriptsApiVersion";
import {
  fetchScriptIdentifiers,
  toScriptApiVersion,
} from "@/utils/scriptVersioning/api";

/**
 * Module-level promise to deduplicate concurrent calls to detectExistingConfiguration.
 * When multiple callers invoke the function at the same time, they share the same in-flight promise.
 */
let pendingDetection: Promise<ConfigurationStatus> | null = null;

/**
 * Represents the configuration status of various onboarding-related features.
 */
export interface ConfigurationStatus {
  /** Whether an inbound script exists */
  hasInboundScript: boolean;
  /** Whether an outbound script exists */
  hasOutboundScript: boolean;
  /** Whether a GailGPT assistant has been created */
  hasAssistant: boolean;
  /** Whether directory contacts have been set up */
  hasDirectory: boolean;
  /** Whether business hours have been configured */
  hasBusinessHours: boolean;
  /** Whether call concurrency has been set */
  hasCallConcurrency: boolean;
  /** Whether FAQs have been created */
  hasFaqs: boolean;
  /** Whether notifications have been configured */
  hasNotifications: boolean;
  /** Whether company info (name, address) is complete */
  hasCompanyInfo: boolean;
  /** Whether a calendar (Gail Calendar or Calendly) is connected */
  hasConnectedCalendar: boolean;
  /** Whether at least one custom voice exists */
  hasCustomVoice: boolean;
  /** Whether any integration is connected */
  hasConnectedIntegration: boolean;
  /** Whether the A2P SMS campaign is approved */
  hasApprovedSmsCampaign: boolean;
  /** Whether at least one GailGPT conversation exists */
  hasConversations: boolean;
}

/**
 * Response type for inbound rules API
 */
type InboundRulesResponse = {
  defaultScriptId: string;
  rules: unknown[];
};

/**
 * Response type for notifications
 */
type NotificationResponse = {
  id: string;
  email: string;
};

/**
 * Detects what configuration already exists for an organization.
 * This allows the onboarding flow to skip steps that have already been completed.
 *
 * @param org - The organization object (contains details like onboarding state)
 * @returns Promise resolving to the configuration status
 */
export function detectExistingConfiguration(org: Org): Promise<ConfigurationStatus> {
  // If a detection is already in progress, share the same promise
  if (pendingDetection) {
    return pendingDetection;
  }

  // Start a new detection and store the promise so concurrent calls can share it
  pendingDetection = doDetectExistingConfiguration(org).finally(() => {
    pendingDetection = null;
  });

  return pendingDetection;
}

async function doDetectExistingConfiguration(org: Org): Promise<ConfigurationStatus> {
  // Initialize with defaults
  const status: ConfigurationStatus = {
    hasInboundScript: false,
    hasOutboundScript: false,
    hasAssistant: false,
    hasDirectory: false,
    hasBusinessHours: false,
    hasCallConcurrency: false,
    hasFaqs: false,
    hasNotifications: false,
    hasCompanyInfo: false,
    hasConnectedCalendar: false,
    hasCustomVoice: false,
    hasConnectedIntegration: false,
    hasApprovedSmsCampaign: false,
    hasConversations: false,
  };

  // Check company info from org object
  status.hasCompanyInfo = Boolean(
    org.name && org.name.trim().length > 0 && org.address?.addressLine1
  );

  // Calendar connection is derivable from the org object, mirroring CalendarPage:
  // Gail Calendar counts once its team and default event type exist; Calendly
  // counts as soon as the feature is selected.
  const calendarApplication = org.features?.calendarApplication;
  status.hasConnectedCalendar =
    (calendarApplication === CALENDAR_APPLICATION.GAIL &&
      Boolean(org.services?.calendarTeamId) &&
      Boolean(org.services?.calendarDefaultEventId)) ||
    calendarApplication === CALENDAR_APPLICATION.CALENDLY;

  // Check call concurrency from org (0 means disabled/cleared, so exclude it)
  status.hasCallConcurrency = org.maxConcurrentCalls != null && org.maxConcurrentCalls > 0;

  // Run API checks in parallel for better performance
  const [
    inboundResult,
    outboundResult,
    directoryResult,
    notificationResult,
    agentProfileResult,
    assistantResult,
    customVoicesResult,
    integrationsResult,
    smsCampaignResult,
    conversationsResult,
  ] = await Promise.allSettled([
    checkInboundScripts(),
    checkOutboundScripts(org),
    checkDirectoryContacts(),
    checkNotifications(),
    checkAgentProfile(),
    checkAssistants(),
    checkCustomVoices(),
    checkConnectedIntegrations(),
    checkSmsCampaignApproved(),
    checkGptConversations(),
  ]);

  // Process results
  if (inboundResult.status === "fulfilled") {
    status.hasInboundScript = inboundResult.value;
  }

  if (outboundResult.status === "fulfilled") {
    status.hasOutboundScript = outboundResult.value;
  }

  if (directoryResult.status === "fulfilled") {
    status.hasDirectory = directoryResult.value;
  }

  if (notificationResult.status === "fulfilled") {
    status.hasNotifications = notificationResult.value;
  }

  if (agentProfileResult.status === "fulfilled") {
    status.hasBusinessHours = agentProfileResult.value.hasBusinessHours;
    status.hasFaqs = agentProfileResult.value.hasFaqs;
  }

  if (assistantResult.status === "fulfilled") {
    status.hasAssistant = assistantResult.value;
  }

  if (customVoicesResult.status === "fulfilled") {
    status.hasCustomVoice = customVoicesResult.value;
  }

  if (integrationsResult.status === "fulfilled") {
    status.hasConnectedIntegration = integrationsResult.value;
  }

  if (smsCampaignResult.status === "fulfilled") {
    status.hasApprovedSmsCampaign = smsCampaignResult.value;
  }

  if (conversationsResult.status === "fulfilled") {
    status.hasConversations = conversationsResult.value;
  }

  return status;
}

// All detection checks use silent=true to suppress console errors,
// since 404s and failures are expected for unconfigured features.

/**
 * Checks if inbound scripts exist
 */
async function checkInboundScripts(): Promise<boolean> {
  try {
    const rules = await apiLive<InboundRulesResponse>(
      "/api/inbound-rules",
      undefined,
      undefined,
      undefined,
      true
    );
    return Boolean(rules?.defaultScriptId);
  } catch {
    return false;
  }
}

/**
 * Checks if outbound scripts exist
 */
async function checkOutboundScripts(org: Org): Promise<boolean> {
  try {
    const scripts = await fetchScriptIdentifiers(
      toScriptApiVersion(isScriptsApiV2(org.features)),
      { direction: "outbound", status: "active" },
      { silent: true }
    );
    return Boolean(scripts && scripts.length > 0);
  } catch {
    return false;
  }
}

/**
 * Checks if directory contacts have been set up
 */
async function checkDirectoryContacts(): Promise<boolean> {
  try {
    const contacts = await apiLive<{ id: string }[]>(
      `${GAIL_API}/.internal/org_directory`,
      undefined,
      undefined,
      undefined,
      true
    );
    return Boolean(contacts && contacts.length > 0);
  } catch {
    return false;
  }
}

/**
 * Checks business hours and FAQs from the agent profile in a single API call.
 * Business hours are in operationSchedule.businessSchedule.
 * FAQs are in frequentlyAskedQuestions.
 */
async function checkAgentProfile(): Promise<{ hasBusinessHours: boolean; hasFaqs: boolean }> {
  try {
    const agentProfile = await apiLive<AgentProfile>(
      `${GAIL_API}/.internal/agent`,
      undefined,
      undefined,
      undefined,
      true
    );

    const schedule = agentProfile?.operationSchedule?.businessSchedule;
    const hasBusinessHours = Boolean(schedule?.some((day) => day.hours?.length > 0));

    const faqs = agentProfile?.frequentlyAskedQuestions;
    const hasFaqs = Boolean(faqs?.some((faq) => faq.question && faq.answer));

    return { hasBusinessHours, hasFaqs };
  } catch {
    return { hasBusinessHours: false, hasFaqs: false };
  }
}

/**
 * Checks if notifications have been configured
 */
async function checkNotifications(): Promise<boolean> {
  try {
    const notifications = await apiLive<NotificationResponse[]>(
      "/api/settings/agency/notifications",
      undefined,
      undefined,
      undefined,
      true
    );
    return Boolean(notifications && notifications.length > 0);
  } catch {
    return false;
  }
}

/**
 * Checks if the org has at least one active GailGPT assistant.
 */
async function checkAssistants(): Promise<boolean> {
  try {
    const response = await apiLive<{ data: Array<{ id: string }> }>(
      `${GAIL_API}/.internal/assistants?limit=1&status=active`,
      undefined,
      undefined,
      undefined,
      true
    );
    return Boolean(response?.data && response.data.length > 0);
  } catch {
    return false;
  }
}

/**
 * Checks if the org has created at least one custom voice.
 */
async function checkCustomVoices(): Promise<boolean> {
  try {
    const voices = await apiLive<unknown[]>(
      "/api/voices/list",
      undefined,
      undefined,
      undefined,
      true
    );
    return Boolean(voices && voices.length > 0);
  } catch {
    return false;
  }
}

/**
 * Checks if any integration is connected, using the same sources as the
 * integrations marketplace: Raccoon connections, agency connections, and the
 * Google node provider.
 */
async function checkConnectedIntegrations(): Promise<boolean> {
  try {
    const [connected, agencyConnections, googleConnected] = await Promise.all([
      getConnectedIntegrations().catch(() => []),
      getAgencyConnections().catch(() => null),
      isNodeProviderConnected("google")
        .then((result) => unwrapSessionExpiry(result, false))
        .catch(() => false),
    ]);
    return Boolean(
      (connected && connected.length > 0) ||
        (agencyConnections && agencyConnections.length > 0) ||
        googleConnected
    );
  } catch {
    return false;
  }
}

/**
 * Checks if the org's A2P SMS campaign is approved. A campaign reports
 * `Verified` when it clears; `Approved` is the brand-level status.
 */
async function checkSmsCampaignApproved(): Promise<boolean> {
  try {
    const status = await getMessagingVerificationStatus();
    return status?.campaignStatus === MessagingVerificationStatus.Verified;
  } catch {
    return false;
  }
}

/**
 * Checks if at least one GailGPT conversation exists (i.e. the user has
 * had a test or real conversation with an assistant).
 */
async function checkGptConversations(): Promise<boolean> {
  try {
    const response = await apiLive<{ data: unknown[]; total?: number }>(
      `${GAIL_API}/.internal/conversations?type=gpt&includeArchived=false&size=1`,
      undefined,
      undefined,
      undefined,
      true
    );
    return Boolean(response && (response.total ?? response.data?.length ?? 0) > 0);
  } catch {
    return false;
  }
}

/**
 * Determines which products the user is likely interested in based on existing configuration.
 * Used to pre-select focus options in the onboarding flow.
 *
 * @param status - The configuration status
 * @returns Array of product focuses that appear to be in use
 */
export function inferProductFocusFromConfig(status: ConfigurationStatus): ProductFocus[] {
  const focuses: ProductFocus[] = [];

  if (status.hasInboundScript || status.hasDirectory) {
    focuses.push("inbound");
  }

  if (status.hasOutboundScript) {
    focuses.push("outbound");
  }

  if (status.hasAssistant) {
    focuses.push("gailgpt");
  }

  return focuses;
}

/**
 * Determines which onboarding steps should be skipped based on existing configuration.
 *
 * @param status - The configuration status
 * @param selectedFocus - The products the user has selected to focus on
 * @param onboardingDetails - Optional onboarding details from the org (used to check tracked script IDs)
 * @returns Object mapping step keys to whether they should be skipped
 */
export function getStepsToSkip(
  status: ConfigurationStatus,
  selectedFocus: ProductFocus[],
  onboardingDetails?: {
    businessHoursScriptId?: string;
    afterHoursScriptId?: string;
    businessId?: string;
    twilioBusinessType?: string;
  }
): Record<string, boolean> {
  const skips: Record<string, boolean> = {};

  const missingTwilioBusinessFields =
    selectedFocus.includes("outbound") &&
    (!onboardingDetails?.businessId || !onboardingDetails?.twilioBusinessType);

  // Shared steps
  skips.companyInfo = status.hasCompanyInfo && !missingTwilioBusinessFields;
  skips.businessHours = status.hasBusinessHours;
  skips.notifications = status.hasNotifications;

  // Inbound-specific steps
  if (selectedFocus.includes("inbound")) {
    skips.directory = status.hasDirectory;
    // Only skip the inbound script step if we already created a business-hours
    // script during onboarding (tracked by businessHoursScriptId).
    // A default sample "Inbound" script from org creation should NOT cause a skip.
    skips.inboundScript = Boolean(onboardingDetails?.businessHoursScriptId);
    // After-hours: skip only if already generated during onboarding
    skips.afterHours = Boolean(onboardingDetails?.afterHoursScriptId);
  }

  // Outbound-specific steps
  if (selectedFocus.includes("outbound")) {
    // New orgs get a backend concurrency default, so a stored value doesn't
    // mean the user made a choice. Always show the step.
    skips.callConcurrency = false;
    skips.outboundScript = status.hasOutboundScript;
    // Campaign setup is always needed for new campaigns
    skips.campaignSetup = false;
  }

  // GailGPT steps
  if (selectedFocus.includes("gailgpt")) {
    skips.gailgpt = status.hasAssistant;
  }

  return skips;
}
